import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Home,
  LayoutDashboard,
  Database,
  Upload,
  Image,
  GitBranch,
  BookOpen,
  GitPullRequest,
  FileText,
  Sparkles,
  Settings,
  Users,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
  FolderOpen,
  ClipboardList,
} from 'lucide-react';
import styles from './Sidebar.module.css';
import { useUIStore } from '../stores/uiStore';
import { useProjectStore } from '../stores/projectStore';
import { useProjects } from '../hooks/useProjects';
import { useAuthStore } from '../stores/authStore';

type NavPathType = 's1000d' | 'html';

interface ProjectNavItem {
  segment: string;
  label: string;
  icon: React.ReactNode;
  pathType?: NavPathType;
  // Application-level Super-User gate. Only one item uses this today
  // (kept available in case future items need it). DMRL Planning used
  // this in Phase 2 but moved to project-scoped permissions in Phase
  // 4.2 (2026-06-03), so the sidebar shows it to every Path A project
  // member and the page's 403 empty state handles users without the
  // required project-level view_dms permission.
  superUserOnly?: boolean;
}

function iconStyle() {
  return { strokeWidth: 1.5, size: 20 } as const;
}

// Project group — ordered exactly as the approved layout. When an
// active project is selected, items whose `pathType` matches the
// project (or who have no `pathType` constraint) render. All links
// resolve to `/projects/:id/<segment>` so they preserve project
// context end-to-end.
const PROJECT_NAV: ProjectNavItem[] = [
  { segment: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard {...iconStyle()} /> },
  { segment: 'csdb', label: 'CSDB', icon: <Database {...iconStyle()} />, pathType: 's1000d' },
  {
    segment: 'dmrl',
    label: 'DMRL Planning',
    icon: <ClipboardList {...iconStyle()} />,
    pathType: 's1000d',
  },
  { segment: 'import', label: 'Import', icon: <Upload {...iconStyle()} />, pathType: 's1000d' },
  { segment: 'graphics', label: 'Graphics & ICN', icon: <Image {...iconStyle()} />, pathType: 's1000d' },
  { segment: 'applicability', label: 'Applicability & CIR', icon: <GitBranch {...iconStyle()} />, pathType: 's1000d' },
  { segment: 'publishing', label: 'Publishing', icon: <BookOpen {...iconStyle()} />, pathType: 's1000d' },
  // HTML Manuals lives at `/projects/:id/manuals` for HTML projects.
  { segment: 'manuals', label: 'HTML Manuals', icon: <FileText {...iconStyle()} />, pathType: 'html' },
  // Review & Changes stays as one surface: link always points at the
  // flat `/review` route. The page has its own project selector and
  // defaults to the current project where supported.
  { segment: '__review__', label: 'Review & Changes', icon: <GitPullRequest {...iconStyle()} /> },
  // AI link points at `/projects/:id/ai` when a project is active.
  { segment: 'ai', label: 'AI', icon: <Sparkles {...iconStyle()} /> },
  { segment: 'settings', label: 'Project Settings', icon: <Settings {...iconStyle()} /> },
  { segment: 'users', label: 'Project Users', icon: <Users {...iconStyle()} /> },
];

function navClass(active: boolean): string {
  return `${styles.navItem} ${active ? styles.active ?? '' : ''}`;
}

// WS-9 follow-up (C7.1.1) — spec/12.8 calls for the sidebar to
// auto-collapse to icons-only within the 1280–1440 tier regardless of
// the user's stored preference. Reactive to live resize so dragging
// the window narrower forces the collapsed state and dragging it wider
// restores the user's preference.
const FORCE_COLLAPSE_BELOW_PX = 1440;

function useScreenForcedCollapse(): boolean {
  const [forced, setForced] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < FORCE_COLLAPSE_BELOW_PX;
  });
  useEffect(() => {
    const apply = (): void => {
      setForced(window.innerWidth < FORCE_COLLAPSE_BELOW_PX);
    };
    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, []);
  return forced;
}

export function Sidebar() {
  const { sidebarExpanded, toggleSidebar } = useUIStore();
  const activeProjectId = useProjectStore((s) => s.activeProjectId);
  const projectsQuery = useProjects('active');
  const me = useAuthStore((s) => s.user);
  const location = useLocation();
  const screenForcedCollapse = useScreenForcedCollapse();
  const effectivelyExpanded = sidebarExpanded && !screenForcedCollapse;

  const activeProject = projectsQuery.data?.find((p) => p.id === activeProjectId) ?? null;
  const isSuperUser = me?.isSuperUser ?? false;

  return (
    <nav
      className={`${styles.root} ${effectivelyExpanded ? '' : styles.collapsed}`}
      aria-label="Primary"
      data-testid="ta-sidebar"
      data-screen-forced-collapse={screenForcedCollapse ? 'true' : 'false'}
    >
      <div className={styles.nav}>
        <NavLink
          to="/projects"
          end
          className={({ isActive }) => navClass(isActive)}
          title={effectivelyExpanded ? undefined : 'Home'}
        >
          <span className={styles.navIcon}>
            <Home {...iconStyle()} />
          </span>
          <span className={styles.navLabel}>Home</span>
        </NavLink>

        {activeProject ? (
          <>
            <hr className={styles.divider} aria-hidden="true" />

            {/* Informational block — NOT a link. Shows the active
                project name + version so users always know which
                project the sidebar items below operate on. */}
            <div
              className={styles.currentProject}
              data-testid="ta-sidebar-current-project"
              title={effectivelyExpanded ? undefined : activeProject.name}
            >
              <FolderOpen
                size={20}
                strokeWidth={1.5}
                className={styles.currentProjectIcon}
                aria-hidden="true"
              />
              <div className={styles.currentProjectMeta}>
                <span className={styles.currentProjectLabel}>Current Project</span>
                <span className={styles.currentProjectName}>{activeProject.name}</span>
                <span className={styles.currentProjectVersion}>
                  {activeProject.pathType === 's1000d'
                    ? `S1000D ${activeProject.s1000dVersion ?? ''}`
                    : 'HTML Manual'}
                </span>
              </div>
            </div>

            <hr className={styles.divider} aria-hidden="true" />

            {PROJECT_NAV.map((item) => {
              if (item.pathType && item.pathType !== activeProject.pathType) return null;
              if (item.superUserOnly && !isSuperUser) return null;
              const to =
                item.segment === '__review__'
                  ? '/review'
                  : `/projects/${activeProject.id}/${item.segment}`;
              return (
                <NavLink
                  key={item.segment}
                  to={to}
                  className={({ isActive }) => navClass(isActive)}
                  title={effectivelyExpanded ? undefined : item.label}
                >
                  <span className={styles.navIcon}>{item.icon}</span>
                  <span className={styles.navLabel}>{item.label}</span>
                </NavLink>
              );
            })}
          </>
        ) : (
          <>
            <hr className={styles.divider} aria-hidden="true" />

            <NavLink
              to="/review"
              className={({ isActive }) => navClass(isActive)}
              title={effectivelyExpanded ? undefined : 'Review & Changes'}
            >
              <span className={styles.navIcon}>
                <GitPullRequest {...iconStyle()} />
              </span>
              <span className={styles.navLabel}>Review &amp; Changes</span>
            </NavLink>
            <NavLink
              to="/ai"
              className={({ isActive }) => navClass(isActive)}
              title={effectivelyExpanded ? undefined : 'AI'}
            >
              <span className={styles.navIcon}>
                <Sparkles {...iconStyle()} />
              </span>
              <span className={styles.navLabel}>AI</span>
            </NavLink>
          </>
        )}

        {isSuperUser ? (
          <>
            <hr className={styles.divider} aria-hidden="true" />
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                navClass(isActive || location.pathname.startsWith('/admin'))
              }
              title={effectivelyExpanded ? undefined : 'Admin'}
            >
              <span className={styles.navIcon}>
                <ShieldCheck {...iconStyle()} />
              </span>
              <span className={styles.navLabel}>Admin</span>
            </NavLink>
          </>
        ) : null}
      </div>

      {/* Bottom footer holds the manual collapse/expand control in
          normal flow so it never overlays sidebar content. Hidden when
          the viewport tier forces the collapsed state. */}
      {!screenForcedCollapse ? (
        <div className={styles.footer}>
          <button
            type="button"
            className={styles.collapseToggle}
            onClick={toggleSidebar}
            aria-label={effectivelyExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {effectivelyExpanded ? (
              <ChevronLeft size={16} strokeWidth={1.5} aria-hidden="true" />
            ) : (
              <ChevronRight size={16} strokeWidth={1.5} aria-hidden="true" />
            )}
          </button>
        </div>
      ) : null}
    </nav>
  );
}
